import { Component } from '@theme/component';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #claimed = false;

  #couponLoaded = false;

  #couponBusy = false;

  #cartBusy = false;

  #couponError = false;

  #errorMode = 'coupon';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('framelune:offerchange', this.#offerChanged);
    this.#renderCoupon();
    if (!this.#campaignActive() && this.dataset.customerId !== 'guest') this.refreshCoupon();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('framelune:offerchange', this.#offerChanged);
  }

  #campaignActive() {
    const now = Date.now() / 1000;
    return this.dataset.campaignApplied === 'true' &&
      now >= Number(this.dataset.campaignStart) && now < Number(this.dataset.campaignEnd);
  }

  #offerChanged = () => {
    this.#renderCoupon();
    if (!this.#campaignActive() && !this.#couponLoaded) this.refreshCoupon();
  };

  updatedCallback() {
    super.updatedCallback();
    this.#offerChanged();
  }

  async #couponRequest(method = 'GET') {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(this.dataset.couponEndpoint || '', {
        method,
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Coupon service unavailable');
      const result = await response.json();
      if (result.code !== this.dataset.couponCode || typeof result.claimed !== 'boolean') {
        throw new Error('Invalid coupon response');
      }
      return result;
    } finally {
      clearTimeout(timeout);
    }
  }

  refreshCoupon = async () => {
    if (this.#couponBusy || this.#campaignActive() || this.dataset.customerId === 'guest') return;
    this.#couponBusy = true;
    this.#couponError = false;
    this.#renderCoupon();
    try {
      const result = await this.#couponRequest();
      this.#claimed = result.claimed;
      this.#couponLoaded = true;
    } catch (_error) {
      this.#couponError = true;
    } finally {
      this.#couponBusy = false;
      this.#renderCoupon();
    }
  };

  #renderCoupon() {
    const campaignActive = this.#campaignActive();
    const campaignNotice = this.querySelector('[data-coupon-campaign]');
    if (campaignNotice) campaignNotice.hidden = !campaignActive;
    const guestLink = this.querySelector('.cart-coupon__signin');
    if (guestLink) guestLink.hidden = campaignActive;
    const wallet = this.querySelector('[data-coupon-wallet]');
    if (!wallet) return;

    const code = this.dataset.couponCode || '';
    const applied = this.#existingDiscounts().some((value) => value.toLowerCase() === code.toLowerCase());
    const claimed = this.#claimed;
    wallet.dataset.state = applied ? 'applied' : claimed ? 'saved' : 'available';
    const claimButton = wallet.querySelector('[data-coupon-claim]');
    const applyButton = wallet.querySelector('[data-coupon-apply]');
    const removeButton = wallet.querySelector('[data-coupon-remove]');
    const status = wallet.querySelector('[data-coupon-status]');
    const conflict = wallet.querySelector('[data-coupon-conflict]');
    const serviceError = wallet.querySelector('[data-coupon-service-error]');
    const loading = wallet.querySelector('[data-coupon-loading]');
    if (claimButton) claimButton.hidden = campaignActive || !this.#couponLoaded || claimed || this.#couponBusy;
    if (applyButton) applyButton.hidden = campaignActive || !claimed || applied || this.#couponBusy;
    if (removeButton) removeButton.hidden = !applied;
    for (const button of [claimButton, applyButton, removeButton]) {
      if (button) button.disabled = this.#cartBusy;
    }
    wallet.setAttribute('aria-busy', String(this.#cartBusy || this.#couponBusy));
    if (status) {
      status.hidden = campaignActive || (!claimed && !applied);
      status.textContent = applied ? status.dataset.labelApplied || '' : status.dataset.labelSaved || '';
    }
    if (serviceError) serviceError.hidden = campaignActive || !this.#couponError;
    if (loading) {
      loading.hidden = campaignActive || (!this.#couponBusy && !this.#cartBusy);
      loading.textContent = this.#cartBusy ? loading.dataset.labelUpdating || '' : loading.dataset.labelLoading || '';
    }
    if (conflict && (campaignActive || applied || this.#existingDiscounts().length === 0)) conflict.hidden = true;
  }

  claimCoupon = async (event) => {
    event.preventDefault();
    if (this.dataset.customerId === 'guest' || this.#campaignActive() || this.#couponBusy || this.#cartBusy) return;
    this.#couponBusy = true;
    this.#couponError = false;
    this.#renderCoupon();
    try {
      const result = await this.#couponRequest('POST');
      if (!result.claimed) throw new Error('Coupon not saved');
      this.#claimed = true;
      this.#couponLoaded = true;
    } catch (_error) {
      this.#couponError = true;
    } finally {
      this.#couponBusy = false;
      this.#renderCoupon();
    }
  };

  applySavedCoupon = (event) => {
    event.preventDefault();
    if (this.#campaignActive()) { this.#renderCoupon(); return; }
    if (this.#cartBusy || (!this.#claimed && !this.#existingDiscounts().includes(this.dataset.couponCode || ''))) return;
    if (this.#existingDiscounts().length > 0) {
      const conflict = this.querySelector('[data-coupon-conflict]');
      if (conflict) conflict.hidden = false;
      return;
    }
    const form = this.querySelector('.cart-discount__form');
    const input = form?.querySelector('input[name="discount"]');
    if (!(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)) return;
    input.value = this.dataset.couponCode || '';
    form.requestSubmit();
  };

  removeSavedCoupon = (event) => {
    event.preventDefault();
    if (this.#cartBusy) return;
    const code = this.dataset.couponCode || '';
    const pill = Array.from(this.querySelectorAll('.cart-discount__pill')).find(
      (item) => item instanceof HTMLLIElement && item.dataset.discountCode?.toLowerCase() === code.toLowerCase()
    );
    pill?.querySelector('button')?.click();
  };

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #sectionFields() {
    const sections = new Set([this.dataset.sectionId]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      sections.add(component.dataset.sectionId);
    });
    return {
      sections: Array.from(sections).filter(Boolean).slice(0, 5),
      sections_url: window.location.pathname,
    };
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();
    if (this.#campaignActive()) { this.#renderCoupon(); return; }
    if (this.#cartBusy) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    this.#errorMode = 'coupon';
    this.#cartBusy = true;
    this.#renderCoupon();

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some((code) => code.toLowerCase() === discountCodeValue.toLowerCase())) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          ...this.#sectionFields(),
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Cart discount update failed');
      const data = await response.json();

      if (
        data.discount_codes?.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === false;
        })
      ) {
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (!newHtml) throw new Error('Cart summary unavailable');
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Check the rendered summary before reloading because a shipping-only discount
        // can be valid in Shopify's response without appearing in the cart totals.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes?.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    } catch (error) {
      if (error?.name !== 'AbortError') this.#handleDiscountError('discount_code');
    } finally {
      this.#cartBusy = false;
      this.#renderCoupon();
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#cartBusy) return;

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    this.#errorMode = 'update';

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    this.#cartBusy = true;
    this.#renderCoupon();
    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), ...this.#sectionFields() }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Cart discount removal failed');
      const data = await response.json();
      if (!data.sections?.[this.dataset.sectionId]) throw new Error('Cart summary unavailable');

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    } catch (error) {
      if (error?.name !== 'AbortError') this.#handleDiscountError('discount_code');
    } finally {
      this.#cartBusy = false;
      this.#renderCoupon();
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    if (type === 'discount_code') {
      for (const mode of ['coupon', 'update']) {
        const message = target.querySelector(`[data-${mode}-error]`);
        if (message) message.hidden = mode !== this.#errorMode;
      }
    }
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
